import { useMemo } from 'react';

import { useMergedRef } from '@/hook';

import type { TRACK_TYPE } from '../VBaseTrack';
import {
  VSliderFilledTrackList,
  VSliderMarkerContainer,
  VSliderTrack,
} from '../VSlider';
import { useVSliderContext } from '../VSlider/hook';
import { createMarkerBuffer } from '../VSlider/util';
import { VStack } from '../VStack';
import { new__cn as cn } from '../util';
import { VCachedRangeFilledTrackList } from './VCachedRangeFilledTrackList';
import { VFrameTrackSliderThumb } from './VFrameTrackSliderThumb';
import { useVFrameTrackSliderContext } from './hook';

export const VInnerFrameTrackSlider = ({
  className,
  ref,
  ...rest
}: React.ComponentPropsWithRef<'div'>) => {
  const { style, $container } = useVSliderContext<TRACK_TYPE.MULTIPLE_RANGE>();
  const { highlightedRange, preloadedRange, value, setValue } =
    useVFrameTrackSliderContext();
  const mergedRef = useMergedRef($container, ref);
  const highlightedRangeBufferList = useMemo(() => {
    if (!highlightedRange) return;

    return highlightedRange.map(([start, end]) =>
      createMarkerBuffer({
        max: end,
        min: start,
        padStart: start,
      }),
    );
  }, [highlightedRange]);
  const preloadedRangeBufferList = useMemo(() => {
    if (!preloadedRange) return;

    return preloadedRange.map(([start, end]) =>
      createMarkerBuffer({
        max: end,
        min: start,
        padStart: start,
      }),
    );
  }, [preloadedRange]);

  return (
    <VStack
      className={cn(style.container, className, 'w-full')}
      role="slider"
      gap={0.25}
      ref={mergedRef}
      {...rest}
    >
      <VSliderTrack
        className="bg-gray-200"
        trackMarkerClassName={value => {
          const isInRange = preloadedRangeBufferList?.some(
            buf => buf[value] === value,
          );

          return isInRange ? cn('bg-gray-400!') : null;
        }}
        onTrackClick={value => setValue(value, 'track')}
      >
        <VCachedRangeFilledTrackList />
        <VSliderFilledTrackList />
      </VSliderTrack>

      <VFrameTrackSliderThumb thumbValue={value} />
      <VSliderMarkerContainer
        markerClassName={value => {
          const isInRange = highlightedRangeBufferList?.some(
            buf => buf[value] === value,
          );

          return cn('font-normal!', {
            'text-gray-300!': !isInRange,
          });
        }}
        onMarkerClick={value => setValue(value, 'marker')}
      />
    </VStack>
  );
};

VInnerFrameTrackSlider.displayName = 'VInnerFrameTrackSlider';
